import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Activity, Loader2, RefreshCw } from "lucide-react";
import AgentJobDetailPanel from "@/components/agentjobs/AgentJobDetailPanel";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { recipesAPI } from "@/services/api/recipes";
import { agentJobsAPI } from "@/services/api/agentJobs";
import { useContextStore } from "@/store/context";
import { formatRuntimeDurationMs } from "@/services/history/executionStatus";
import ExecutionRow from "@/components/execution/ExecutionRow";
import {
  agentJobExecutionRow,
  workflowExecutionRow,
} from "@/components/execution/executionState";
import { cn } from "@/utils/cn";

function executionVariant(state: string) {
  if (["completed", "complete", "succeeded"].includes(state)) return "success";
  if (["failed", "errored", "cancelled"].includes(state)) return "destructive";
  if (["running", "applying"].includes(state)) return "secondary";
  return "outline";
}


/**
 * Workflow executions and AgentJobs of an Agent. With `workflowId`, the view
 * is the Executions tab of that Workflow (lot R4): runs filtered server-side
 * on the Workflow, without the Application-wide AgentJobs list.
 */
export default function AgentSpecExecutionObservability({
  organizationId,
  agentId,
  workflowId,
}: {
  organizationId: string;
  agentId: string;
  workflowId?: string;
}) {
  const { t } = useTranslation();
  const applicationKey = useContextStore((state) => state.applicationKey);
  const application = useContextStore((state) =>
    state.applications.find(
      (item) =>
        item.key === applicationKey &&
        item.organization_id === organizationId &&
        item.status === "active",
    ),
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedExecutionId = searchParams.get("executionId");
  const [selectedExecutionId, setSelectedExecutionId] = useState<string | null>(
    linkedExecutionId,
  );
  const linkedAgentJobId = searchParams.get("agentJobId");
  const [selectedAgentJobId, setSelectedAgentJobId] = useState<string | null>(
    linkedAgentJobId,
  );
  useEffect(() => {
    if (linkedExecutionId) setSelectedExecutionId(linkedExecutionId);
  }, [linkedExecutionId]);
  useEffect(() => {
    setSelectedAgentJobId(linkedAgentJobId);
  }, [linkedAgentJobId]);
  const executionsQuery = useQuery({
    queryKey: ["agent-workflow-executions", organizationId, agentId, workflowId ?? null],
    queryFn: () =>
      recipesAPI.listOrganizationRecipeExecutions(organizationId, agentId, {
        limit: 50,
        ...(workflowId ? { workflow_id: workflowId } : {}),
      }),
    refetchInterval: 5_000,
  });
  const executionQuery = useQuery({
    queryKey: [
      "agent-workflow-execution",
      organizationId,
      agentId,
      selectedExecutionId,
    ],
    queryFn: () =>
      recipesAPI.getScopedRecipeExecutionStatus(
        organizationId,
        agentId,
        selectedExecutionId!,
        { include_logs: true, include_trace: true },
      ),
    enabled: Boolean(selectedExecutionId),
    refetchInterval: (query) =>
      ["running", "pending", "draft"].includes(
        String(query.state.data?.state ?? ""),
      )
        ? 2_000
        : false,
  });
  const jobsQuery = useQuery({
    queryKey: ["agent-jobs", organizationId, application?.id],
    queryFn: () => agentJobsAPI.list(organizationId, application!.id),
    enabled: Boolean(application?.id) && !workflowId,
    refetchInterval: 2_000,
    retry: false,
  });
  const selectAgentJob = (jobId?: string) => {
    setSelectedAgentJobId(jobId ?? null);
    const next = new URLSearchParams(searchParams);
    if (jobId) next.set("agentJobId", jobId);
    else next.delete("agentJobId");
    setSearchParams(next, { replace: true });
  };

  return (
    <>
    <div className="grid min-h-[32rem] gap-4 xl:grid-cols-[minmax(18rem,0.8fr)_minmax(0,1.4fr)]">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle>{t("agentSpec.executionsTitle")}</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("agentSpec.executionsDescription")}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => void executionsQuery.refetch()}
            aria-label={t("agentSpec.refreshExecutions")}
          >
            <RefreshCw
              className={cn(
                "h-4 w-4",
                executionsQuery.isFetching &&
                  "animate-spin motion-reduce:animate-none",
              )}
            />
          </Button>
        </CardHeader>
        <CardContent>
          {executionsQuery.isLoading ? (
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-accent-foreground motion-reduce:animate-none" />
          ) : executionsQuery.data?.data.length ? (
            <div className="space-y-2">
              {executionsQuery.data.data.map((execution) => (
                <ExecutionRow
                  key={execution.execution_id}
                  testId={`agent-spec-execution-${execution.execution_id}`}
                  model={workflowExecutionRow(execution)}
                  selected={selectedExecutionId === execution.execution_id}
                  onSelect={() => {
                    setSelectedExecutionId(execution.execution_id);
                    const next = new URLSearchParams(searchParams);
                    next.set("executionId", execution.execution_id);
                    setSearchParams(next, { replace: true });
                  }}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t("agentSpec.executionsEmpty")}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-accent-foreground" />
            {t("agentSpec.executionDetails")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {!selectedExecutionId ? (
            <p className="text-sm text-muted-foreground">
              {t("agentSpec.selectExecution")}
            </p>
          ) : executionQuery.isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-accent-foreground motion-reduce:animate-none" />
          ) : executionQuery.data ? (
            <>
              <dl className="grid gap-3 text-sm sm:grid-cols-3">
                <div>
                  <dt className="text-muted-foreground">{t("agentSpec.status")}</dt>
                  <dd><Badge variant={executionVariant(executionQuery.data.state)}>{executionQuery.data.state}</Badge></dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t("agentSpec.duration")}</dt>
                  <dd>
                    {formatRuntimeDurationMs(executionQuery.data.duration_ms) ??
                      t("historyDetail.notAvailable")}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t("agentSpec.traceId")}</dt>
                  <dd className="truncate font-mono text-xs" title={executionQuery.data.trace_id}>{executionQuery.data.trace_id || "—"}</dd>
                </div>
              </dl>
              {executionQuery.data.usage_summary ? (
                <dl className="grid gap-3 rounded-lg border border-border p-4 text-sm sm:grid-cols-3">
                  <div><dt className="text-muted-foreground">{t("agentSpec.llmCalls")}</dt><dd>{executionQuery.data.usage_summary.llm.calls}</dd></div>
                  <div><dt className="text-muted-foreground">{t("agentSpec.tokens")}</dt><dd>{executionQuery.data.usage_summary.llm.tokens_total}</dd></div>
                  <div><dt className="text-muted-foreground">{t("agentSpec.estimatedCost")}</dt><dd>${executionQuery.data.usage_summary.cost.estimated_cost_usd.toFixed(6)}</dd></div>
                </dl>
              ) : null}
              {executionQuery.data.error ? (
                <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  {executionQuery.data.error}
                </p>
              ) : null}
              <div>
                <h3 className="mb-2 font-semibold">{t("agentSpec.executionLogs")}</h3>
                <pre className="max-h-80 overflow-auto rounded-xl bg-muted p-4 font-mono text-xs text-foreground">
                  {(executionQuery.data.logs ?? [])
                    .map((log) => `${log.level ?? "info"} ${log.component ?? "runtime"} — ${log.message ?? ""}`)
                    .join("\n") || t("agentSpec.executionLogsEmpty")}
                </pre>
              </div>
              <div>
                <h3 className="mb-2 font-semibold">{t("agentSpec.executionResult")}</h3>
                <pre className="max-h-80 overflow-auto rounded-xl bg-muted p-4 font-mono text-xs text-foreground">
                  {JSON.stringify(executionQuery.data.result ?? executionQuery.data.data ?? {}, null, 2)}
                </pre>
              </div>
            </>
          ) : (
            <p role="alert" className="text-sm text-destructive">
              {t("agentSpec.executionLoadError")}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
    {workflowId ? null : (
    <section className="mt-6" aria-label={t("agentJob.title")}>
      <Card>
        <CardHeader>
          <CardTitle>{t("agentJob.title")}</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">{t("agentJob.description", { defaultValue: t("agentSpec.executionsDescription") })}</p>
        </CardHeader>
        <CardContent>
          {!application ? (
            <p className="text-sm text-muted-foreground">{t("agentJob.inaccessible")}</p>
          ) : jobsQuery.isLoading ? (
            <Loader2 className="mx-auto h-5 w-5 animate-spin text-accent-foreground motion-reduce:animate-none" />
          ) : jobsQuery.isError ? (
            <p role="alert" className="text-sm text-destructive">{t("agentJob.error")}</p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
              <div className="space-y-2">
                {(jobsQuery.data?.jobs ?? []).map((item) => (
                  <ExecutionRow
                    key={item.job.job_id}
                    testId={`agent-spec-job-${item.job.job_id}`}
                    model={agentJobExecutionRow(item)}
                    selected={selectedAgentJobId === item.job.job_id}
                    onSelect={() => selectAgentJob(item.job.job_id)}
                  />
                ))}
                {!jobsQuery.data?.jobs?.length ? <p className="text-sm text-muted-foreground">{t("agentJob.empty")}</p> : null}
              </div>
              <AgentJobDetailPanel organizationId={organizationId} applicationId={application.id} jobId={selectedAgentJobId} onSelectedJob={selectAgentJob} buildRetryParentHref={(parentJobId) => { const next = new URLSearchParams(searchParams); next.set("agentJobId", parentJobId); return `/studio/agent-spec?${next.toString()}` }} />
            </div>
          )}
        </CardContent>
      </Card>
    </section>
    )}
    </>
  );
}
